import { cn, withUrl } from "@/lib/utils";
import PrimaryButton from "@/components/ui/primary-button";
import LINKS from "@/lib/links";
import BenefitsCard from "@/components/ui/benefits-card";
import { useWindowSize } from "@/hooks/use-window-size";
import { handleSmoothScroll } from "@/lib/utils/smooth-scroll";

type Cta = {
  href: string;
  text: string;
  ariaLabel: string;
};

export interface HeroCiAppProps {
  id?: string;
  className?: string;
  heading?: React.ReactNode | string;
  subheading?: React.ReactNode | string;
  primaryCta?: Cta;
  cards?: {
    title: string;
    subtitle: string;
    body: string;
  }[];
}

const defaultProps: HeroCiAppProps = {
  heading: "Smart Staffing for Smarter Care",
  subheading:
    "Care Indeed introduces the all-new Care Indeed App – your ultimate staffing solution. Built for care facilities, our platform simplifies scheduling, tracking, and communication so your team delivers better care with less hassle. ",
  primaryCta: {
    href: withUrl(LINKS.careAssessment.href),
    text: "Request a Demo",
    ariaLabel: "Request a Demo",
  },
  cards: [
    {
      title: "Shift Management",
      subtitle: "Scheduling",
      body: "Fill positions faster with smart scheduling that matches the right staff to each shift. No more overtime headaches or coverage gaps.",
    },
    {
      title: "Unified Credentialing",
      subtitle: "Compliance",
      body: "One secure hub for all staff licenses and certifications. Automated tracking ensures you're always compliant.",
    },
    {
      title: " 24/7 Care Team Chat",
      subtitle: "Support",
      body: "Never face a staffing crisis alone. Human support is ready whenever you call.",
    },
    {
      title: "Streamlined Billing",
      subtitle: "Invoicing",
      body: "Simplify your process with automated invoicing that integrates with scheduling data. Get paid faster, with less effort.",
    },
  ],
};

const HeroCiApp: React.FC<HeroCiAppProps> = ({
  id = "hero",
  className,
  heading,
  subheading,
  primaryCta,
  cards,
}) => {
  heading = heading ?? defaultProps.heading;
  subheading = subheading ?? defaultProps.subheading;
  primaryCta = primaryCta ?? defaultProps.primaryCta;
  cards = cards ?? defaultProps.cards;
  const size = useWindowSize();

  return (
    <section
      id={id}
      aria-label={id}
      className={cn(
        "mx-auto flex max-w-lg flex-col gap-4 p-4 md:gap-6 md:px-6 md:py-8 lg:gap-8 lg:px-3 lg:py-10",
        className
      )}
    >
      <div className="flex flex-col gap-2 md:gap-4 lg:gap-6">
        <div className="text-center font-montserrat text-heading-3 text-secondary-500 md:text-heading-2 lg:text-heading-1">
          {heading}
        </div>
        <div className="text-center font-montserrat text-subheading-3-500 text-neutral-500 md:text-heading-4 lg:text-heading-3">
          {subheading}
        </div>
      </div>
      <div className="z-10 flex justify-center">
        <PrimaryButton
          href={
            size === "md" || size === "sm"
              ? `${primaryCta?.href}-section`
              : primaryCta?.href
          }
          size={size === "lg" ? "lg" : "md"}
          disabled={!Boolean(primaryCta?.href)}
          color="orange"
          onClick={(
            e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
          ) =>
            handleSmoothScroll(
              e as React.MouseEvent<HTMLAnchorElement>,
              size === "md" || size === "sm"
                ? `${primaryCta?.href}-section`
                : (primaryCta?.href ?? ""),
              size === "md" ? 50 : size === "sm" ? 25 : 80
            )
          }
        >
          {primaryCta?.text}
        </PrimaryButton>
      </div>
      <div className="grid w-fit grid-cols-1 gap-4 self-center md:grid-cols-2 md:gap-6 lg:grid-cols-4 lg:gap-11">
        {cards?.map((card) => (
          <BenefitsCard
            key={card.title}
            title={card.title}
            subtitle={card.subtitle}
            body={card.body}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroCiApp;
